import { PRICING_PLANS } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { useIntercom } from "@/hooks/useIntercom";
import { useOrganization } from "@/hooks/useOrganization";
import useSearchParams from "@/hooks/useSearchParams";
import { getExternalPricingURL } from "@/lib/pricing";
import { cn } from "@/lib/utils";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import {
  CheckIcon,
  ExternalLinkIcon,
  InfoIcon,
  MessagesSquareIcon,
} from "lucide-react";

export default function UpsaleModal() {
  const intercom = useIntercom();

  // URL state
  const [searchParams, setSearchParams] = useSearchParams<{
    upsale?: boolean | null;
  }>();

  const { formatCurrency, currency } = useI18n();

  // Server state
  const { data: orgData } = useOrganization();
  const { plan, country } = orgData?.ok ? orgData.data : {};

  if (!plan || !country) {
    return null;
  }

  const pricingPlans = PRICING_PLANS[country];

  if (!pricingPlans?.length) {
    return null;
  }

  // Helpers
  const externalPricingURL = getExternalPricingURL(country);

  return (
    <Dialog
      open={!!searchParams.upsale}
      onOpenChange={(newOpen) => {
        setSearchParams({
          upsale: newOpen ? true : null,
        });
      }}
    >
      <DialogContent className="max-w-5xl!">
        <DialogHeader className="text-center! items-center">
          <DialogTitle>
            <Trans>Få full tillgång</Trans>
          </DialogTitle>
          <DialogDescription className="max-w-xl">
            <Trans>
              Skalbara planer för alla behov. Börja i liten skala och väx i din
              egen takt. Letar du efter våra API-lösningar? Se våra
              skräddarsydda priser{" "}
              <a
                className="underline underline-offset-4"
                href={externalPricingURL}
                rel="noopener noreferrer"
                target="_blank"
              >
                här
              </a>
              .
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex py-4 pr-4">
          {pricingPlans.map((PLAN, i) => {
            return (
              <Card
                key={PLAN.planType}
                className={cn(
                  "flex-1 gap-6 py-10",
                  i % 2 === 0 ? "bg-muted" : "bg-white scale-105"
                )}
              >
                <CardHeader className="px-10 gap-3">
                  <CardTitle className="text-lg">
                    {i18n._(PLAN.title)}
                  </CardTitle>
                  <CardDescription>{i18n._(PLAN.description)}</CardDescription>
                </CardHeader>
                <CardContent className="px-10 flex flex-col gap-8">
                  {"value" in PLAN.price ? (
                    <p>
                      <span className="text-4xl font-semibold leading-none">
                        {formatCurrency(PLAN.price.value[currency], currency)}
                      </span>{" "}
                      <span className="text-muted-foreground">
                        {i18n._(PLAN.price.billing)}
                      </span>
                    </p>
                  ) : (
                    <p className="text-4xl font-semibold leading-none">
                      {i18n._(PLAN.price)}
                    </p>
                  )}

                  <div className="flex flex-col gap-4">
                    <p className="font-medium text-sm">
                      <Trans>Vad ingår:</Trans>
                    </p>

                    <ul className="flex flex-col gap-4">
                      {PLAN.features.map((feature, i) => (
                        <li
                          key={`${PLAN.planType}-feature-${i}`}
                          className="flex items-center gap-2.5"
                        >
                          <CheckIcon className="size-5" />

                          <p className="text-muted-foreground text-sm">
                            {i18n._(feature.label)}
                          </p>

                          <Tooltip>
                            <TooltipTrigger className="ml-auto">
                              <InfoIcon className="size-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-sm text-center">
                                {i18n._(feature.info)}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            render={
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={externalPricingURL}
              />
            }
            nativeButton={false}
            variant="outline"
          >
            <Trans>Prisdetaljer</Trans>
            <ExternalLinkIcon />
          </Button>
          {intercom.show && (
            <Button
              type="button"
              onClick={() => {
                intercom.show();
              }}
            >
              <Trans>Kontakta oss</Trans>
              <MessagesSquareIcon />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
